/**
 * Keep dimension line-item properties in sync with the selected variant so
 * checkout shows the same "Single pot" / "Sets of N Planter Dimensions" info as cart.
 */
function updateDimensionLineProperties(root, variant) {
  if (!root || !variant) return;

  const containers = root.querySelectorAll('.js-dimension-line-properties');
  containers.forEach((container) => {
    let variantMap = {};
    try {
      variantMap = JSON.parse(container.dataset.dimensionVariants || '{}');
    } catch (e) {
      variantMap = {};
    }

    const props = variantMap[String(variant.id)] || null;
    container.querySelectorAll('.js-dimension-prop-input').forEach((input) => input.remove());

    if (!props) return;

    Object.keys(props).forEach((name) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.className = 'js-dimension-prop-input';
      input.name = `properties[${name}]`;
      input.value = props[name];
      container.appendChild(input);
    });
  });
}

function isDimensionPropertyName(name) {
  const down = String(name || '').toLowerCase();
  return (
    down === 'single pot' ||
    down === 'case planter dimensions' ||
    down.includes('planter dimensions')
  );
}

async function syncCartDimensionProperties() {
  const rows = document.querySelectorAll(
    '.cart-item[data-dimension-props][data-has-dimension-prop="false"]'
  );
  if (!rows.length) return false;

  let updated = false;

  for (const row of rows) {
    const lineKey = row.dataset.lineKey;
    const qty = Number(row.dataset.lineQty || 1);
    if (!lineKey) continue;

    let dimensionProps = {};
    let existingProps = {};
    try {
      dimensionProps = JSON.parse(row.dataset.dimensionProps || '{}');
      existingProps = JSON.parse(row.dataset.existingProps || '{}');
    } catch (e) {
      continue;
    }

    if (!dimensionProps || !Object.keys(dimensionProps).length) continue;

    const merged = { ...existingProps };
    Object.keys(merged).forEach((key) => {
      if (isDimensionPropertyName(key)) delete merged[key];
    });
    Object.assign(merged, dimensionProps);

    try {
      const response = await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: qty,
          properties: merged
        })
      });
      if (response.ok) {
        row.dataset.hasDimensionProp = 'true';
        updated = true;
      }
    } catch (e) {
      // Allow checkout to continue even if sync fails.
    }
  }

  return updated;
}

document.addEventListener('on:variant:change', (event) => {
  const variant = event.detail.variant;
  const product = event.detail.product;
  const section = event.target.closest('.js-product');

  if (section) {
    updateDimensionLineProperties(section, variant);

    const titleEl = section.querySelector('.js-product-title-text');
    if (titleEl) {
      const baseTitle = titleEl.dataset.baseTitle || '';
      let colorValue = '';
      let sizeValue = '';

      if (product && product.options && variant && variant.options) {
        const colorIndex = product.options.findIndex((name) =>
          String(name).toLowerCase().includes('color')
        );
        const sizeIndex = product.options.findIndex((name) =>
          String(name).toLowerCase().includes('size')
        );

        colorValue = colorIndex >= 0 ? variant.options[colorIndex] || '' : '';
        sizeValue = sizeIndex >= 0 ? variant.options[sizeIndex] || '' : '';
      }

      let variantSuffix = '';
      if (sizeValue) {
        variantSuffix = sizeValue;
        if (colorValue) variantSuffix += ` (${colorValue})`;
      } else if (colorValue) {
        variantSuffix = colorValue;
      }

      const suffixEl = titleEl.querySelector('.js-product-title-variant-suffix');
      const linkEl = titleEl.querySelector('.js-product-title-link');

      if (suffixEl) {
        let displaySuffix = '';
        if (sizeValue) {
          displaySuffix = ` - ${sizeValue}`;
          if (colorValue) displaySuffix += ` (${colorValue})`;
        } else if (colorValue) {
          displaySuffix = ` - ${colorValue}`;
        }
        suffixEl.textContent = displaySuffix;
        if (linkEl) {
          linkEl.textContent = baseTitle;
        }
      } else {
        let textNode = Array.from(titleEl.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE
        );
        if (!textNode) {
          textNode = document.createTextNode('');
          titleEl.prepend(textNode);
        }
        textNode.nodeValue = variantSuffix ? `${baseTitle} - ${variantSuffix} ` : `${baseTitle} `;
      }
    }
  }

  if (variant) {
    const sku = variant.sku;
    const skuElement = document.querySelector('#product-sku-my');
    if (skuElement) {
      skuElement.textContent = sku ? `SKU: ${sku}` : 'SKU: Not available';
    }
  } else {
    console.log('No variant selected');
  }
});

let dimensionCartSyncPromise = null;

function ensureCartDimensionPropertiesSynced() {
  if (dimensionCartSyncPromise) return dimensionCartSyncPromise;

  const needsSync = document.querySelector(
    '.cart-item[data-dimension-props][data-has-dimension-prop="false"]'
  );
  if (!needsSync) return Promise.resolve(false);

  dimensionCartSyncPromise = syncCartDimensionProperties().finally(() => {
    dimensionCartSyncPromise = null;
  });
  return dimensionCartSyncPromise;
}

document.addEventListener(
  'click',
  async (event) => {
    const checkoutBtn = event.target.closest('button[name="checkout"]');
    if (!checkoutBtn) return;

    const needsSync = document.querySelector(
      '.cart-item[data-dimension-props][data-has-dimension-prop="false"]'
    );
    if (!needsSync) return;

    event.preventDefault();
    event.stopPropagation();

    checkoutBtn.disabled = true;
    try {
      await ensureCartDimensionPropertiesSynced();
    } finally {
      checkoutBtn.disabled = false;
    }

    const form = checkoutBtn.form || checkoutBtn.closest('form');
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(checkoutBtn);
      } else {
        form.submit();
      }
    } else {
      window.location.href = '/checkout';
    }
  },
  true
);

// Sync when cart is present so Shop Pay / accelerated checkout also get properties.
document.addEventListener('DOMContentLoaded', () => {
  ensureCartDimensionPropertiesSynced();
});

document.addEventListener('on:cart:add', () => {
  setTimeout(() => {
    ensureCartDimensionPropertiesSynced();
  }, 400);
});

document.addEventListener('on:cart-drawer:after-open', () => {
  ensureCartDimensionPropertiesSynced();
});
/* Remove horizontal scroll from homepage slider on mobile */
document.addEventListener('DOMContentLoaded', () => {
  const mobileBreakpoint = 768;

  function updateSliders() {
    const sliders = document.querySelectorAll(
      '.cc-collection-list .slider, .cc-featured-collection .slider'
    );

    sliders.forEach((slider) => {
      slider.classList.toggle(
        'slider--no-scrollbar',
        window.innerWidth < mobileBreakpoint
      );
    });
  }

  // Initial load
  updateSliders();

  // On resize/orientation change
  window.addEventListener('resize', updateSliders);
  window.addEventListener('orientationchange', updateSliders);

  // If the slider is re-rendered during swipe
  const observer = new MutationObserver(updateSliders);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
  // document.addEventListener("DOMContentLoaded", function () {
  //   console.log("7");
  //   const submitBtn = document.querySelector(".submit-form");

  //   if (submitBtn) {
  //     submitBtn.addEventListener("click", function (e) {
  //       // Optional: prevent default form submission if you want delay before it submits
  //       e.preventDefault();

  //       // Wait 3 seconds, then redirect
  //       setTimeout(function () {
  //         window.location.href = "https://plantatorem.com/pages/free-design-consultation-thank-you";
  //       }, 7000);
  //     });
  //   }
  // });
/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

